// @ts-check
import { AxiosError } from 'axios'; // Importing AxiosError type for type checking
import type { UserI } from '../types/user';
import { api } from './calls';
import { handleAxiosError } from './utils';

/**
 * Fetches the current user's information
 * @returns {Promise<{user: import('../types/user').UserI}>} User information
 * @throws {import('../types/api.ts').ApiErrorResponse} When API request fails
 * @throws {Error} When response data is invalid
 */
const getUser = async (): Promise<{
  user_id: string
  user_name: string
  user_email: string
  country_code: string
  user_phone_number: string
  user_birthday: string
  newsletter: boolean
  marketing_and_offers: boolean
  text_message: boolean
}> => {
  try {
    return await api.get('/users/me', {
      timeout: 5000, // 5 second timeout
    })
  } catch (e) {
    // Type checking the error to ensure it is of AxiosError type or a general Error
    if (e instanceof AxiosError || e instanceof Error) {
      throw handleAxiosError(e) // Handle Axios or Error instances
    } else {
      throw handleAxiosError(new Error('An unknown error occurred')) // Fallback for unknown error types
    }
  }
}

/**
 * @param {import('../types/user.ts').UserI} user
 * @returns {Promise<{user: import('../types/user').UserI}>}
 * @throws {import('../types/api.ts').ApiErrorResponse} When API request fails
 * @throws {Error} When response data is invalid
 */
const updateUser = async (user: UserI): Promise<{ user: UserI } | null> => {
  try {
    const res = await api.put('/me', user)

    // Basic validation of response data
    if (!res.data || typeof res.data !== 'object') {
      throw new Error('Invalid response data')
    }

    return res.data
  } catch (e) {
    // Type checking the error to ensure it is of AxiosError type or a general Error
    if (e instanceof AxiosError || e instanceof Error) {
      throw handleAxiosError(e) // Handle Axios or Error instances
    } else {
      throw handleAxiosError(new Error('An unknown error occurred')) // Fallback for unknown error types
    }
  }
}

export { getUser, updateUser };

